<template>
  <div class="fi-container">
    <transition name="fade-up" appear>
      <div class="fi-box">
        <h2>Select an FI</h2>

        <q2-select
          ref="fiSearchSelect"
          @input="onInput"
          @change="onChange"
          @keyup.enter="submit"
          label="FI Search"
          placeholder="Search FI Number or Name"
          searchable="true"
          required
        >
          <q2-option
            v-for="fi in allFis"
            :key="fi.fiNumber"
            :value="fi.fiNumber"
          >
            {{ fi.fiNumber }} - {{ fi.fiName }}
          </q2-option>
        </q2-select>

        <q2-btn intent="workflow-primary" class="submit-btn" @click="submit">
          Submit
        </q2-btn>

        <p v-if="error" class="error-msg">{{ error }}</p>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, onMounted, onUnmounted, nextTick} from "vue";
import { useRouter } from "vue-router";
import { useFiStore } from "../stores/buildStores";
import { useAuthStore } from "../stores/authStore";
import { logger } from "../utils/logger";
import { Q2Select } from "q2-tecton-framework-wrappers/dist/vue";

const fiSearchSelect = ref<typeof Q2Select | null>(null);

async function focusWhenReady(element: HTMLElement) {
  // eslint-disable-next-line
  if (typeof (element as any).componentOnReady === "function") {
    // eslint-disable-next-line
    await (element as any).componentOnReady();
  }

  // eslint-disable-next-line
  const el = (element as any).$el ?? element;
  if (el?.focus) {
    el.focus();
  }
}

onMounted(async () => {
  await nextTick(); // ensure the DOM ref is resolved

  if (fiSearchSelect.value) {
    await focusWhenReady(fiSearchSelect.value);
  }
});

onUnmounted(() => {
  logger.debug("🧹 getFInumber.vue unmounted");
});

const fiStore = useFiStore();
const authStore = useAuthStore();
const fiNumber = ref("");
const error = ref("");
const router = useRouter();

const allFis = computed(() => authStore.allFis);

function onInput(event: CustomEvent) {
  // Handles typing in the search field - event.detail contains { query: string }
  const query = event.detail?.query ?? "";
  logger.debug("Search query:", query);

  // Only update fiNumber when user is actively typing (query is not empty)
  // This prevents the dropdown selection from being cleared when the component
  // clears the search field after selecting an item
  if (query) {
    fiNumber.value = query;
  }
}

function onChange(event: CustomEvent) {
  // Handles selecting an option from the dropdown - event.detail contains { value: string, selectedOptions: string[] }
  const value = event.detail?.value ?? event.detail;
  logger.debug("Selected value:", value);

  fiNumber.value = value;
}

function submit() {
  let finalFiNumber = fiNumber.value;
  // If fiNumber is empty or not set, reject
  if (!finalFiNumber) {
    error.value = "FI number is required";
    return;
  }

  // If the value isn't from a selected option, validate it's an integer
  const isValidOption = allFis.value.some(fi => fi.fiNumber === finalFiNumber);
  if (!isValidOption) {
    // Check if it's a valid integer
    if (!/^\d+$/.test(finalFiNumber)) {
      error.value = "Please select a valid FI or enter an FI number";
      return;
    }
    logger.debug("📝 Using manually entered FI number:", finalFiNumber);
  } else {
    logger.debug("✅ Using selected FI option:", finalFiNumber);
  }

  fiStore.setFiNumber(finalFiNumber);
  logger.debug("📅 Selected FI Number:", finalFiNumber);
  error.value = "";

  router.push({
    name: "FIAppsDynamic",
    params: {
      fiNumber: fiStore.getFiNumber,
    },
  });
}
</script>

<style scoped>
.fi-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #fdf8ff;
  width: 100%;
  overflow: hidden;
  padding: 0;
  margin: 0;
  --tct-btn-primary-background: #594bab;
  --tct-btn-primary-active-background: #594bab;
  --tct-btn-primary-box-shadow: none;
  --tct-btn-border-radius: 5px;
  --tct-btn-padding: 12px 20px;
  --tct-input-background: #ffffff;
  --tct-input-label-font-color: #404040;
  --tct-input-label-padding: 0 0 6px 0;
}

.fi-box {
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  width: 450px;
  min-width: 400px;
  color: #404040;
}

.submit-btn {
  margin-top: 20px;
  width: 100%;
}

.error-msg {
  color: red;
  margin-top: 10px;
}

.fade-up-enter-active {
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.fade-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.fade-up-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.h2 {
  color: #404040;
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .fi-container {
    padding: 20px;
  }

  .fi-box {
    width: auto;
    min-width: unset;
    max-width: none;
    padding: 24px 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    margin: 0 20px;
  }

  .fi-box h2 {
    font-size: 24px;
    margin-bottom: 20px;
  }

  .submit-btn {
    margin-top: 24px;
    min-height: 48px;
    font-size: 16px;
  }
}

/* Extra small mobile devices */
@media (max-width: 480px) {
  .fi-container {
    padding: 16px;
  }

  .fi-box {
    padding: 20px 16px;
    border-radius: 4px;
    margin: 0 16px;
  }

  .fi-box h2 {
    font-size: 20px;
  }
}
</style>
